"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Sun, Clock, TrendingUp, Calendar, BookOpen, Telescope, RotateCcw } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function SolarArcPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-orange-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-orange-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-orange-500">/</span>
                <span className="text-sm">Solar Arc Direktleri</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Solar Arc Direktleri</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-orange-900/20 to-amber-900/20 rounded-xl p-8 mb-8 border border-orange-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Sun className="h-10 w-10 text-orange-400" />
                Solar Arc Direktleri
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Güneş'in yıllık hareket miktarı olan yaklaşık 1°'lik ilerlemeyi tüm gezegenlere uygulayarak
                gelecekteki potansiyel olayları ve gelişimleri tahmin eden güçlü zamanlama tekniği.
                Modern astrolojinin en güvenilir prognostik yöntemlerinden biri.
              </p>
            </div>

            {/* Basic Concept */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Kavram</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <RotateCcw className="h-5 w-5 text-orange-400" />
                      Solar Arc Prensibi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Güneş bir yılda ortalama 59' 08" (yaklaşık 1°) ilerler.
                        Bu miktar tüm gezegenlere eklenerek yeni pozisyonlar hesaplanır.
                      </p>
                      <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-orange-200 mb-2">Temel Formül:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Solar Arc = Güneş'in doğum tarihinden bugüne kadar olan hareketi</div>
                          <div>Yeni Pozisyon = Natal Pozisyon + Solar Arc</div>
                          <div>Ortalama: 1° per yıl (daha kesin: 0.985647°/yıl)</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-400" />
                      Hesaplama Yöntemi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Doğum gününden hedef tarihe kadar geçen günler x 0.985647° / 365.25
                      </p>
                      <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-200 mb-2">Örnek Hesaplama (25 yaş):</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Natal Merkür: 15° Boğa</div>
                          <div>Solar Arc (25 yıl): 25° x 0.985647 = 24° 38'</div>
                          <div>SA Merkür: 15° Boğa + 24° 38' = 9° 38' İkizler</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Historical Development */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">Claudius Ptolemy (MS 2. yy)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Solar Arc sisteminin temelleri Ptolemy'nin "Tetrabiblos" eserinde
                      açıklandığı "progressed Sun" konseptine dayanır.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Antik kökenler:</strong> Güneş'in yıllık hareketi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Progression kavramı:</strong> Zaman ile mesafe eşitliği</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Tetrabiblos:</strong> İlk teorik açıklama</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-green-300">Modern Dönem (20. yy)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Charles Jayne ve Noel Tyl gibi astrologlar tarafından sistemleştirildi.
                      Bilgisayar teknolojisi ile yaygınlaştı.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Charles Jayne:</strong> Kesin hesaplama yöntemleri</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Noel Tyl:</strong> Modern uygulama teknikleri</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Bilgisayar yazılımları:</strong> Otomatik hesaplama</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Major Solar Arc Aspects */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Önemli Solar Arc Aspectleri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* SA Sun Aspects */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                      <Sun className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">SA Güneş Aspectleri</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Kişilik ve Yaşam Yönü</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">SA Güneş conj Merkür: Zihinsel gelişim</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">SA Güneş conj Venüs: Aşk, sanat, finansal başarı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-400">SA Güneş conj MC: Kariyer zirvesi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SA Moon Aspects */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☽</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">SA Ay Aspectleri</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Duygusal Değişimler</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">SA Ay conj Venüs: Romantik ilişkiler</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">SA Ay conj Jüpiter: Duygusal genişleme</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">SA Ay conj IC: Aile, ev değişimleri</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SA Mercury Aspects */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☿</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">SA Merkür Aspectleri</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İletişim ve Öğrenme</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">SA Merkür conj Jüpiter: Yayıncılık, öğretmenlik</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">SA Merkür conj Satürn: Ciddileşme, odaklanma</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">SA Merkür conj Uranüs: Teknoloji, yenilik</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SA Venus Aspects */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♀</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">SA Venüs Aspectleri</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Aşk, Sanat, Finans</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">SA Venüs conj Mars: Tutku, çekim</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">SA Venüs conj Jüpiter: Bolluk, şans</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-pink-400 rounded-full"></div>
                        <span className="text-slate-400">SA Venüs conj DSC: Evlilik, ortaklık</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SA Mars Aspects */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♂</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">SA Mars Aspectleri</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Eylem ve Enerji</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">SA Mars conj Güneş: Liderlik, inisiyatif</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">SA Mars conj Satürn: Kontrollü güç</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">SA Mars conj MC: Kariyer hamlesi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SA Outer Planet Aspects */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♇</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">SA Dış Gezegen Aspectleri</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Transformasyon</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">SA ASC conj Pluto: Kimlik dönüşümü</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">SA MC conj Uranüs: Ani kariyer değişimi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">SA Güneş conj Neptün: Ruhsal uyanış</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timing and Orbs */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Zamanlama ve Orb Kullanımı</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-teal-400" />
                    Etki Süresi ve Orb'lar
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Solar Arc aspectleri genellikle kesin olduğu tarih etrafında
                      ±1 yıl etkili olur. Orb kullanımı critical öneme sahiptir.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Conjunction:</strong> ±1° (en güçlü etki)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Opposition:</strong> ±1° (kriz ve fırsat)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Square:</strong> ±30' (challenge ve gelişim)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Trine/Sextile:</strong> ±30' (kolaylık ve fırsat)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-orange-400" />
                    Aktivasyon Safhaları
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Solar Arc aspectleri üç safhada kendini gösterir:
                      Yaklaşma, Exact (kesin), Uzaklaşma.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Yaklaşma (-1 yıl):</strong> Potansiyelin ortaya çıkışı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Exact (0):</strong> En yoğun manifestasyon</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Uzaklaşma (+1 yıl):</strong> Entegrasyon ve sonuçlar</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Pratik Uygulama</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-400" />
                      Analiz Adımları
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-200 mb-2">Sistematik Yaklaşım:</h4>
                        <div className="space-y-2 text-sm text-slate-300">
                          <div>1. Mevcut yılın Solar Arc pozisyonlarını hesapla</div>
                          <div>2. Natal harita ile exact aspectleri bul</div>
                          <div>3. Aspect türü ve dahil olan gezegenleri değerlendir</div>
                          <div>4. House pozisyonlarını kontrol et</div>
                          <div>5. Diğer prognostik tekniklerle karşılaştır</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-400" />
                      Yorumlama İpuçları
                    </h3>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Luminaries (Güneş/Ay) en güçlü etkiye sahiptir</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Angles (ASC/MC/DSC/IC) önemli yaşam değişimleri</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Outer planetlerle aspectler transformasyon dönemleri</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Natal aspect yapılarını destekleyen SA'ler daha güçlü</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Örnek Vaka Analizi</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-emerald-300">Kariyer Değişimi</h3>
                  <div className="space-y-3">
                    <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-lg p-4">
                      <div className="font-medium text-emerald-200 mb-2">SA Güneş conjunction MC (32 yaş)</div>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div>• Kişi hayatının en büyük kariyer atılımını yaşar</div>
                        <div>• Liderlik pozisyonuna geçiş</div>
                        <div>• Public recognition ve ün</div>
                        <div>• Yaşam amacının kristalleşmesi</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-rose-300">Evlilik Zamanlaması</h3>
                  <div className="space-y-3">
                    <div className="bg-rose-900/20 border border-rose-700/30 rounded-lg p-4">
                      <div className="font-medium text-rose-200 mb-2">SA Venüs conjunction DSC (28 yaş)</div>
                      <div className="text-sm text-slate-300 space-y-2">
                        <div>• Significant partnership başlangıcı</div>
                        <div>• Romantik ilişkide derinleşme</div>
                        <div>• Marriage commitment</div>
                        <div>• Değerler sisteminde ortaklık</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Solar Arcs" - Noel Tyl</div>
                    <div>• "Predictive Astrology" - Frances Sakoian</div>
                    <div>• "Solar Arc Directions" - Carole Devine</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">İleri Düzey Çalışmalar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "The Art of Prediction" - Carol Rushman</div>
                    <div>• "Dynamics of Aspect Analysis" - Bil Tierney</div>
                    <div>• "Progressions, Solar Arcs & Transits" - Maritha Pottenger</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Solar Arc Direktleri matematiksel hesaplamalara dayanan ileri düzey bir timing tekniğidir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Profesyonel konsultasyon için deneyimli astrologlarla çalışılması önerilir.
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/vertex"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Vertex Analizi
              </Link>

              <Link
                href="/about/astrology/advanced-systems/progressed"
                className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
              >
                Progressed Haritalar
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}